// PocketBase client singleton for PPS
//
// Multi-tenant: auth is Google OAuth2 only (no email/password).
// All data access uses PB's per-user API rules (user_id = @request.auth.id).

import PocketBase, { LocalAuthStore } from 'pocketbase';
import { Capacitor } from '@capacitor/core';

// ─── Configuration ───
// In development, Vite proxies /api/* to PocketBase (see vite.config.ts).
// In production, PocketBase API is on the same origin via Caddy reverse proxy.
// Override with VITE_PB_URL env var if PB is on a different domain.
//
// Sub-path detection: when served under an Okelo public app route (/p/{slug}/),
// the PB SDK must prefix API calls with the app's path segment so Caddy can
// route them to PocketBase.  On a standalone domain (e.g. pps.nip.io), the
// origin alone is correct.
declare global {
  interface ImportMetaEnv {
    readonly VITE_PB_URL?: string;
  }
  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}
/** Dedicated PocketBase origin for the native shell. Its API is served at the
 * origin root; adding the browser-only /pps prefix returns the app HTML rather
 * than JSON and makes every native sign-in fail. */
const NATIVE_PB_URL = 'https://pps.141.95.95.190.nip.io';

export function resolvePBUrl(origin: string, pathname: string, isNative: boolean): string {
  if (isNative) return NATIVE_PB_URL;
  const ppsMatch = pathname.match(/^(\/pps)\//);
  if (ppsMatch) return origin + ppsMatch[1];
  const pathMatch = pathname.match(/^(\/p\/[^/]+)\//);
  if (pathMatch) return origin + pathMatch[1];
  return origin;
}

function detectPBUrl(): string {
  if (import.meta.env.VITE_PB_URL) return import.meta.env.VITE_PB_URL;
  const { origin, pathname } = window.location;
  return resolvePBUrl(origin, pathname, Capacitor.isNativePlatform());
}
const PB_URL = detectPBUrl();

// ─── Singleton ───
const authStore = new LocalAuthStore('jps_pb_auth');
const pb = new PocketBase(PB_URL, authStore);

// ─── Re-export pb instance for direct access ───
export { pb };
export default pb;

// ─── PocketBase type helpers ───
// These mirror the backend collections created via the admin API.

export interface PBWaypoint {
  id: string;
  name: string;
  description: string;
  lat: number;
  lng: number;
  shape?: string;
  color?: string;
  icon?: string;
  tags: string[];
  photos: string[];
  folder_id?: string;
  created?: number;
  updated?: number;
  user_id: string;
}

export interface PBTrack {
  id: string;
  name: string;
  points: string; // JSON-encoded array of {lat, lon, ele?, time?}
  distance: number;
  duration: number;
  start_time?: number;
  end_time?: number;
  folder_id?: string;
  updated?: number;
  user_id: string;
}

export interface PBFolder {
  id: string;
  name: string;
  parent_id?: string;
  created?: number;
  user_id: string;
}

export interface PBSetting {
  id: string;
  key: string;
  value: unknown;
  user_id: string;
}

// ─── Settings sync helpers ──────────────────────────────────────────
// Push/pull individual settings to/from the PocketBase settings collection.
// Fire-and-forget on write (IndexedDB is source of truth); graceful-null on read.

/** Check if a PB API error is an auth failure (401). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isAuthError(err: any): boolean {
  return err?.status === 401;
}

/**
 * Upsert a single setting key/value pair to the PocketBase settings collection.
 * Does NOT block the caller on network — errors are logged and swallowed.
 */
export async function saveSettingToPB(key: string, value: unknown): Promise<void> {
  if (!pb.authStore.isValid) return;

  try {
    const result = await pb.collection('settings').getList(1, 1, {
      filter: `key = "${key}"`,
    });

    if (result.items.length > 0) {
      await pb.collection('settings').update(result.items[0].id, { value });
    } else {
      await pb.collection('settings').create({
        key,
        value,
        user_id: (pb.authStore.record as { id: string }).id,
      });
    }
  } catch (err: unknown) {
    // If the token expired, notify the app so it can prompt re-login
    if (isAuthError(err)) {
      console.warn('[PPS] Auth expired during save — prompting re-login');
      window.dispatchEvent(new CustomEvent('pps:auth-expired'));
    } else {
      console.warn('[PPS] Failed to save setting to PB:', key, err);
    }
  }
}

/**
 * Load a single setting from PocketBase. Returns null if not found,
 * not authenticated, or the network call fails.
 */
export async function loadSettingFromPB<T>(key: string): Promise<T | null> {
  if (!pb.authStore.isValid) return null;

  try {
    const result = await pb.collection('settings').getList(1, 1, {
      filter: `key = "${key}"`,
    });

    if (result.items.length > 0) {
      return result.items[0].value as T;
    }
    return null;
  } catch (err: unknown) {
    if (isAuthError(err)) {
      console.warn('[PPS] Auth expired during load — prompting re-login');
      window.dispatchEvent(new CustomEvent('pps:auth-expired'));
    } else {
      console.warn('[PPS] Failed to load setting from PB:', key, err);
    }
    return null;
  }
}
